import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Loader2, TrendingUp, Building2, Users, Package, ListFilter, X, Upload, Download,
  Image as ImageIcon, MapPin, CalendarClock, FileStack,
} from "lucide-react";

const MACRO = {
  RCA: { label: "RCA", cls: "bg-indigo-100 text-indigo-700 border-indigo-200", color: "#6366f1" },
  RE: { label: "Rami Elementari", cls: "bg-emerald-100 text-emerald-700 border-emerald-200", color: "#10b981" },
  VITA: { label: "Vita", cls: "bg-amber-100 text-amber-700 border-amber-200", color: "#f59e0b" },
};
const NICCHIE = ["R.C. Auto", "Auto (comparto)", "Infortuni / Salute", "R.C. Diversi / Professionale",
  "Multirischi / Danni ai beni", "Tutela Legale", "Furto", "Cauzioni", "Catastrofi Naturali", "Vita", "Altri Rami Elementari"];
const PALETTE = ["#7c3aed", "#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#ec4899", "#14b8a6", "#8b5cf6", "#f97316"];
const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const eurShort = (n) => (Math.abs(n) >= 1000 ? `${(n / 1000).toFixed(0)}k` : `${n}`);

const EMPTY = {
  stato: "attive", date_basis: "", dfrom: "", dto: "", compagnia: "", produttore: "", macro: "",
  nicchia: "", ramo: "", prodotto: "", provincia: "", regione: "", frazionamento: "", comune: "", targa: "", q: "",
};

export default function PortafoglioPanel() {
  const [f, setF] = useState(EMPTY);
  const [esclC, setEsclC] = useState(() => sessionStorage.getItem("pf_escl_cauzioni") === "1");
  const [esclT, setEsclT] = useState(() => sessionStorage.getItem("pf_escl_temporanee") === "1");
  useEffect(() => { sessionStorage.setItem("pf_escl_cauzioni", esclC ? "1" : "0"); }, [esclC]);
  useEffect(() => { sessionStorage.setItem("pf_escl_temporanee", esclT ? "1" : "0"); }, [esclT]);
  const [cfg, setCfg] = useState({ compagnie: [], produttori: [], rami: [], province: [], regioni: [], frazionamenti: [], dataset: {} });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("compagnia");
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const fileRef = useRef(null);
  const chartsRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const loadCfg = useCallback(async () => {
    try { const { data } = await api.get("/direzione/portafoglio/config"); setCfg(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  const params = useCallback(() => {
    const p = {};
    Object.entries(f).forEach(([k, v]) => { if (v) p[k] = v; });
    p.stato = f.stato;
    if (esclC) p.escl_cauzioni = true;
    if (esclT) p.escl_temporanee = true;
    return p;
  }, [f, esclC, esclT]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/direzione/portafoglio/overview", { params: params() }); setData(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const doImport = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/direzione/portafoglio/import", fd);
      toast.success(`Importate ${data.polizze} polizze (${data.attive} attive, ${data.annullate} annullate)`);
      await loadCfg(); await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/direzione/portafoglio/export.xlsx", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `portafoglio_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setExporting(false); }
  };

  const doExportPng = async () => {
    if (!chartsRef.current) return;
    try {
      const u = await toPng(chartsRef.current, { backgroundColor: "#ffffff", pixelRatio: 2, skipFonts: true });
      const a = document.createElement("a"); a.href = u; a.download = `portafoglio_grafici_${new Date().toISOString().slice(0, 10)}.png`; a.click();
    } catch { toast.error("Impossibile generare l'immagine"); }
  };

  const totale = data?.totale || 0;
  const pct = (v) => (totale > 0 ? Math.round((v / totale) * 100) : 0);
  const activeFilters = Object.entries(f).filter(([k, v]) => v && k !== "stato").length + (f.stato !== "attive" ? 1 : 0);

  const compChart = (data?.by_compagnia || []).slice(0, 8).map((r) => ({ name: r.compagnia, v: r.premio }));
  const prodChart = (data?.by_produttore || []).slice(0, 8).map((r) => ({ name: r.produttore, v: r.premio }));
  const provChart = (data?.by_provincia || []).slice(0, 8).map((r) => ({ name: r.provincia, v: r.premio }));
  const macroChart = ["RCA", "RE", "VITA"].map((m) => ({ name: MACRO[m].label, value: data?.by_macro?.[m] || 0, color: MACRO[m].color }));

  const TABS = [
    { key: "compagnia", label: "Compagnia", icon: Building2, rows: data?.by_compagnia, col: "compagnia" },
    { key: "produttore", label: "Collaboratore", icon: Users, rows: data?.by_produttore, col: "produttore" },
    { key: "prodotto", label: "Prodotto", icon: Package, rows: data?.by_prodotto, col: "prodotto" },
    { key: "ramo", label: "Ramo", icon: ListFilter, rows: data?.by_ramo, col: "ramo" },
    { key: "provincia", label: "Provincia", icon: MapPin, rows: data?.by_provincia, col: "provincia" },
    { key: "frazionamento", label: "Frazionamento", icon: FileStack, rows: data?.by_frazionamento, col: "frazionamento" },
    { key: "scadenze", label: "Scadenze", icon: CalendarClock, rows: (data?.scadenze || []).map((s) => ({ ...s, mese: s.mese })), col: "mese" },
  ];
  const activeTab = TABS.find((t) => t.key === tab);

  const Fs = ({ label, value, onChange, options, allLabel, w, testid }) => (
    <div>
      <label className="text-xs text-slate-500 block mb-1">{label}</label>
      <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
        <SelectTrigger className={`h-10 ${w}`} data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-72"><SelectItem value="all">{allLabel}</SelectItem>
          {(options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  return (
    <div data-testid="dir-portafoglio">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-slate-500">
          Stock polizze in essere · metrica <b>Imponibile annuo attuale</b> · {cfg.dataset?.polizze || 0} polizze ({cfg.dataset?.attive || 0} attive)
          {cfg.dataset?.import?.filename ? ` · ${cfg.dataset.import.filename}` : ""}
        </p>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" className="hidden" onChange={(e) => doImport(e.target.files?.[0])} data-testid="pf-import-input" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="pf-import-btn">
            {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Importa Portafoglio</Button>
        </div>
      </div>

      {/* Filtri */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-6" data-testid="pf-filters">
        <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-slate-100">
          <span className="text-xs font-medium text-slate-500 mr-1">Portafoglio vivo:</span>
          <button onClick={() => setEsclC((v) => !v)} data-testid="pf-escl-cauzioni"
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${esclC ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}>
            {esclC ? "✓ " : ""}Escludi Cauzioni
          </button>
          <button onClick={() => setEsclT((v) => !v)} data-testid="pf-escl-temporanee"
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${esclT ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}>
            {esclT ? "✓ " : ""}Escludi Temporanee / Premio unico
          </button>
          {(esclC || esclT) && <span className="text-[11px] text-violet-600 ml-1">Visualizzazione portafoglio ricorrente</span>}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-slate-500 block mb-1">Stato</label>
            <div className="flex rounded-lg border border-slate-200 overflow-hidden">
              {[["attive", "Attive"], ["annullate", "Annullate"], ["tutte", "Tutte"]].map(([k, l]) => (
                <button key={k} onClick={() => set("stato", k)} className={`px-3 py-2 text-sm ${f.stato === k ? "bg-violet-600 text-white" : "bg-white text-slate-600"}`} data-testid={`pf-stato-${k}`}>{l}</button>
              ))}
            </div>
          </div>
          <Fs label="Compagnia" testid="pf-compagnia" value={f.compagnia} onChange={(v) => set("compagnia", v)} options={cfg.compagnie} allLabel="Tutte" w="w-48" />
          <Fs label="Collaboratore" testid="pf-produttore" value={f.produttore} onChange={(v) => set("produttore", v)} options={cfg.produttori} allLabel="Tutti" w="w-44" />
          <div>
            <label className="text-xs text-slate-500 block mb-1">Macro-ramo</label>
            <Select value={f.macro || "all"} onValueChange={(v) => set("macro", v === "all" ? "" : v)}>
              <SelectTrigger className="h-10 w-40" data-testid="pf-macro"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti</SelectItem><SelectItem value="RCA">RCA</SelectItem>
                <SelectItem value="RE">Rami Elementari</SelectItem><SelectItem value="VITA">Vita</SelectItem></SelectContent>
            </Select>
          </div>
          <Fs label="Nicchia" testid="pf-nicchia" value={f.nicchia} onChange={(v) => set("nicchia", v)} options={NICCHIE} allLabel="Tutte" w="w-52" />
          <Fs label="Provincia" testid="pf-provincia" value={f.provincia} onChange={(v) => set("provincia", v)} options={cfg.province} allLabel="Tutte" w="w-28" />
          <Fs label="Regione" testid="pf-regione" value={f.regione} onChange={(v) => set("regione", v)} options={cfg.regioni} allLabel="Tutte" w="w-40" />
          <Fs label="Frazionamento" testid="pf-frazionamento" value={f.frazionamento} onChange={(v) => set("frazionamento", v)} options={cfg.frazionamenti} allLabel="Tutti" w="w-36" />
          <Fs label="Ramo (grezzo)" testid="pf-ramo" value={f.ramo} onChange={(v) => set("ramo", v)} options={cfg.rami} allLabel="Tutti" w="w-44" />
          <div><label className="text-xs text-slate-500 block mb-1">Prodotto</label>
            <Input value={f.prodotto} onChange={(e) => set("prodotto", e.target.value)} placeholder="es. auto…" className="h-10 w-36" data-testid="pf-prodotto" /></div>
          <div><label className="text-xs text-slate-500 block mb-1">Targa</label>
            <Input value={f.targa} onChange={(e) => set("targa", e.target.value)} placeholder="targa" className="h-10 w-28" data-testid="pf-targa" /></div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Scadenza da/a</label>
            <div className="flex items-center gap-1">
              <Input type="date" value={f.date_basis === "scadenza" ? f.dfrom : ""} onChange={(e) => setF((p) => ({ ...p, date_basis: "scadenza", dfrom: e.target.value }))} className="h-10 w-36" data-testid="pf-scad-from" />
              <Input type="date" value={f.date_basis === "scadenza" ? f.dto : ""} onChange={(e) => setF((p) => ({ ...p, date_basis: "scadenza", dto: e.target.value }))} className="h-10 w-36" data-testid="pf-scad-to" />
            </div>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-xs text-slate-500 block mb-1">Ricerca libera</label>
            <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="contraente, polizza, CF, targa…" className="h-10" data-testid="pf-search" /></div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          {activeFilters > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setF(EMPTY)} data-testid="pf-reset" className="text-slate-500"><X className="h-4 w-4 mr-1" /> Azzera filtri ({activeFilters})</Button>)}
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={doExportPng} data-testid="pf-export-png"><ImageIcon className="h-4 w-4 mr-1.5" /> Grafici PNG</Button>
            <Button size="sm" onClick={doExport} disabled={exporting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="pf-export-xlsx">
              {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Scarica Excel</Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-16 flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="rounded-xl border border-violet-200 bg-violet-50 p-5" data-testid="pf-kpi-totale">
              <div className="flex items-center gap-2 text-violet-700 mb-1"><TrendingUp className="h-5 w-5" /><span className="text-sm font-medium">Portafoglio annuo</span></div>
              <p className="text-2xl font-bold text-slate-900">{eur(totale)}</p>
              <p className="text-xs text-slate-500 mt-1">{data?.polizze || 0} polizze · imponibile</p>
            </div>
            {["RCA", "RE", "VITA"].map((m) => (
              <div key={m} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`pf-kpi-${m}`}>
                <Badge variant="outline" className={MACRO[m].cls}>{MACRO[m].label}</Badge>
                <p className="text-xl font-bold text-slate-900 mt-2">{eur(data?.by_macro?.[m])}</p>
                <p className="text-xs text-slate-500 mt-1">{pct(data?.by_macro?.[m])}% del totale</p>
              </div>
            ))}
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="pf-kpi-province">
              <div className="flex items-center gap-2 text-slate-600 mb-1"><MapPin className="h-5 w-5" /><span className="text-sm font-medium">Province</span></div>
              <p className="text-2xl font-bold text-slate-900">{(data?.by_provincia || []).length}</p>
            </div>
          </div>

          <div ref={chartsRef} className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-testid="pf-charts">
            <ChartCard title="Per Compagnia" className="lg:col-span-2">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={compChart} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={50} />
                  <YAxis tickFormatter={eurShort} tick={{ fontSize: 11 }} /><Tooltip formatter={(v) => eur(v)} />
                  <Bar dataKey="v" radius={[6, 6, 0, 0]}>{compChart.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Per Macro-ramo">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={macroChart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label={(e) => eurShort(e.value)}>
                    {macroChart.map((e, i) => <Cell key={i} fill={e.color} />)}</Pie>
                  <Tooltip formatter={(v) => eur(v)} /><Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Per Provincia">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={provChart} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                  <XAxis type="number" tickFormatter={eurShort} tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={40} />
                  <Tooltip formatter={(v) => eur(v)} /><Bar dataKey="v" fill="#0ea5e9" radius={[0, 5, 5, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Per Collaboratore" className="lg:col-span-3">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={prodChart} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={50} />
                  <YAxis tickFormatter={eurShort} tick={{ fontSize: 11 }} /><Tooltip formatter={(v) => eur(v)} />
                  <Bar dataKey="v" fill="#7c3aed" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="flex flex-wrap border-b border-slate-100" data-testid="pf-table-tabs">
              {TABS.map((t) => { const Icon = t.icon; return (
                <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-1.5 px-4 py-3 text-sm border-b-2 -mb-px ${tab === t.key ? "border-violet-600 text-violet-700 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"}`} data-testid={`pf-tab-${t.key}`}>
                  <Icon className="h-4 w-4" /> {t.label}</button>); })}
            </div>
            <table className="w-full text-sm" data-testid={`pf-table-${tab}`}>
              <thead className="text-xs text-slate-400 bg-slate-50"><tr>
                <th className="text-left px-4 py-2 font-medium">{tab === "scadenze" ? "Mese scadenza" : activeTab.label}</th>
                {tab === "ramo" && <th className="text-left px-4 py-2 font-medium">Macro</th>}
                <th className="text-right px-4 py-2 font-medium">Polizze</th>
                <th className="text-right px-4 py-2 font-medium">Imponibile annuo</th>
                <th className="text-right px-4 py-2 font-medium w-16">%</th>
              </tr></thead>
              <tbody>
                {(activeTab.rows || []).map((r, i) => (
                  <tr key={i} className="border-t border-slate-50 hover:bg-slate-50">
                    <td className="px-4 py-2.5 text-slate-700">{tab === "ramo" ? r.nicchia : (r[activeTab.col] || "—")}</td>
                    {tab === "ramo" && <td className="px-4 py-2.5"><Badge variant="outline" className={MACRO[r.macro]?.cls}>{r.macro}</Badge></td>}
                    <td className="px-4 py-2.5 text-right text-slate-400 text-xs">{r.count}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-slate-900">{eur(r.premio)}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-slate-400">{pct(r.premio)}%</td>
                  </tr>
                ))}
                {!(activeTab.rows || []).length && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Nessun dato per i filtri selezionati</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function ChartCard({ title, children, className = "" }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-4 ${className}`}>
      <h3 className="font-heading font-semibold text-slate-800 text-sm mb-3">{title}</h3>
      {children}
    </div>
  );
}
